import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { EASE } from "./Reveal";

export const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -92, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LINKS = [
  { label: "The Craft", href: "#craft", testid: "nav-link-craft" },
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Work", href: "#work", testid: "nav-link-work" },
  { label: "Reviews", href: "#reviews", testid: "nav-link-reviews" },
  { label: "Agents", href: "#agents", testid: "nav-link-agents" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (href) => {
    setMenuOpen(false);
    scrollToId(href);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -90 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled
          ? "bg-coal/85 backdrop-blur-md border-b border-line"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-24 h-[88px]">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center gap-2 group"
        >
          <img
            src="/logo-full.png"
            alt="After Paint logo"
            data-testid="nav-logo-img"
            className="h-16 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => scrollToId(l.href)}
              className="text-xs uppercase tracking-[0.2em] text-smoke hover:text-bone transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          <a
            data-testid="nav-phone-link"
            href="tel:0410514499"
            className="hidden xl:flex items-center gap-2 text-xs tracking-[0.15em] font-medium text-bone hover:text-ember transition-colors duration-300"
          >
            <Phone size={13} strokeWidth={1.5} />
            0410 514 499
          </a>
          <motion.button
            data-testid="nav-quote-button"
            whileTap={{ scale: 0.96 }}
            onClick={() => scrollToId("#quote")}
            className="border border-ember/50 text-ember text-[11px] uppercase tracking-[0.2em] font-semibold px-5 py-2 rounded-full hover:bg-ember hover:text-coal hover:border-ember transition-colors duration-300"
          >
            Free Quote
          </motion.button>
          <button
            data-testid="nav-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className="md:hidden text-bone hover:text-ember transition-colors duration-300"
          >
            {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="md:hidden fixed inset-0 top-[88px] z-40 bg-coal/[0.98] backdrop-blur-md flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-${l.testid}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.08 + i * 0.06, ease: EASE }}
                  onClick={() => go(l.href)}
                  className="text-left font-serif font-light text-4xl tracking-tight py-3 border-b border-line text-bone hover:text-ember transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <motion.a
              data-testid="mobile-phone-link"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              href="tel:0410514499"
              className="mt-10 inline-flex items-center gap-3 text-sm tracking-[0.15em] text-smoke"
            >
              <Phone size={15} strokeWidth={1.5} className="text-ember" />
              0410 514 499
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
